import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Button, LoadingSpinner } from '~/components';
import { getBillingHistory, getBillingSchedules, getBillingSummary } from './api';
import {
  formatCalendarDate,
  formatCardBrand,
  formatCardExpiry,
  formatCents,
  formatDate,
  formatDollars,
  formatMaskedLast4,
  formatRateBps,
} from './format';
import {
  BillingEventType,
  BillingHistoryRow,
  BillingSchedule,
  BillingSummary,
  PaymentMethodSummary,
} from './types';
import { useManagePaymentMethod } from './useManagePaymentMethod';

const describePaymentMethod = (method: PaymentMethodSummary) => {
  if (method.type === 'card') {
    return {
      primary: `${formatCardBrand(method.brand)} ${formatMaskedLast4(method.last4)}`,
      secondary: formatCardExpiry(method.expMonth, method.expYear),
    };
  }
  return {
    primary: `${method.bankName || 'Bank account'} ${formatMaskedLast4(method.last4)}`,
    secondary: null as string | null,
  };
};

// [MCV-1858] The recurring platform fee must never take the rest of the billing page down with
// it: a failed read just hides the section.
const loadSchedules = async (): Promise<BillingSchedule[]> => {
  try {
    return (await getBillingSchedules()) ?? [];
  } catch (err) {
    console.error('Failed to load billing schedules', err);
    return [];
  }
};

const SCHEDULE_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  STOPPED: 'Stopped',
  COMPLETED: 'Completed',
};

// [MCV-1933] Every event type has its own label; a row from a server that predates event types is
// the one-time charge, now called the commitment.
const HISTORY_TYPE_LABEL: Record<BillingEventType, string> = {
  MATCH_ACTIVATION: 'Commitment',
  PLATFORM_FEE: 'Platform fee',
  PERM_BALANCE: 'Placement fee balance',
  LISTING_FEE: 'Listing fee',
  ENTERPRISE_PLAN: 'Enterprise listing plan',
};

const describeHistoryType = (row: BillingHistoryRow) =>
  HISTORY_TYPE_LABEL[row.eventType ?? 'MATCH_ACTIVATION'] ?? 'Commitment';

// [MCV-1933] Credit disclosure. STANDARD credits are capped at 50% of each fee invoice; a balance
// made only of LEGACY credits (granted before current pricing) covers commitments and fees in full.
const LEGACY_CREDIT_DISCLOSURE =
  'Legacy credit covers commitments, platform fees and placement fees up to your remaining legacy balance, but not listing fees or enterprise plans.';
const STANDARD_CREDIT_DISCLOSURE =
  "Promotional credits can cover up to 50% of each platform or placement fee invoice. They don't apply to commitments or listing fees.";

const describeCreditDisclosure = (credits: BillingSummary['activeCredits']) => {
  if (credits.length === 0) return null;
  return [
    credits.some((credit) => credit.redemptionRule === 'LEGACY') ? LEGACY_CREDIT_DISCLOSURE : null,
    credits.some((credit) => credit.redemptionRule !== 'LEGACY')
      ? STANDARD_CREDIT_DISCLOSURE
      : null,
  ]
    .filter(Boolean)
    .join(' ');
};

const describeHistoryPeriod = (row: BillingHistoryRow) =>
  row.periodStart && row.periodEnd
    ? `${formatCalendarDate(row.periodStart)} – ${formatCalendarDate(row.periodEnd)}`
    : '—';

export const BillingPage = () => {
  const addPaymentMethod = useManagePaymentMethod('replace');
  const managePaymentMethods = useManagePaymentMethod('manage');
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [history, setHistory] = useState<BillingHistoryRow[]>([]);
  const [schedules, setSchedules] = useState<BillingSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([getBillingSummary(), getBillingHistory(), loadSchedules()])
      .then(([summaryData, historyData, scheduleData]) => {
        if (!mounted) return;
        setSummary(summaryData);
        setHistory(historyData);
        setSchedules(scheduleData);
      })
      .catch((err) => {
        console.error('Failed to load billing data', err);
        if (!mounted) return;
        setError('Billing information could not be loaded. Please try again.');
        setSummary(null);
        setHistory([]);
        setSchedules([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center mt-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <section className="p-5 sm:p-8 bg-white rounded-xl shadow-sm border border-grey-900 w-full mx-auto">
        <h2 className="sub-heading-sb mb-2 text-left text-grey-100">Billing</h2>
        <p className="body-text text-error">{error ?? 'Billing information is unavailable.'}</p>
      </section>
    );
  }

  const paymentMethodDescription = summary.paymentMethod
    ? describePaymentMethod(summary.paymentMethod)
    : null;
  const creditDisclosure = describeCreditDisclosure(summary.activeCredits);
  const listingPlan = summary.listingPlan ?? null;

  return (
    <section className="p-5 sm:p-8 bg-white rounded-xl shadow-sm border border-grey-900 w-full mx-auto">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-5 sm:pb-6 mb-5 sm:mb-6 border-b border-grey-900">
        <div>
          <h2 className="sub-heading-sb mb-2 text-left text-grey-100">Billing</h2>
          <p className="body-text text-left text-grey-400">
            Payment method, promotional credits, commitments, platform fees, and listing fees.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {summary.paymentMethod ? (
            <Button type="button" onClick={managePaymentMethods}>
              <span className="inline-flex items-center gap-2">
                <CreditCardIcon className="h-5 w-5" />
                Manage payment methods
              </span>
            </Button>
          ) : (
            <Button type="button" onClick={addPaymentMethod}>
              <span className="inline-flex items-center gap-2">
                <CreditCardIcon className="h-5 w-5" />
                Add payment method
              </span>
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className="rounded-lg border border-grey-900 p-4">
          <p className="body-text text-grey-400">Promotional credit balance</p>
          <p className="sub-heading-sb text-grey-100 mt-2">
            {formatCents(summary.creditBalanceCents)}
          </p>
          {creditDisclosure && (
            <p
              className="body-text text-grey-400 mt-2 break-words"
              data-cy="billing-credit-disclosure"
            >
              {creditDisclosure}
            </p>
          )}
        </div>
        <div className="rounded-lg border border-grey-900 p-4">
          <p className="body-text text-grey-400">Payment method</p>
          {paymentMethodDescription ? (
            <>
              <p className="sub-heading-sb text-grey-100 mt-2">
                {paymentMethodDescription.primary}
              </p>
              {paymentMethodDescription.secondary && (
                <p className="body-text text-grey-400 mt-1">{paymentMethodDescription.secondary}</p>
              )}
            </>
          ) : (
            <p className="sub-heading-sb text-grey-100 mt-2">Missing</p>
          )}
        </div>
        <div className="rounded-lg border border-grey-900 p-4">
          <p className="body-text text-grey-400">Active credits</p>
          <p className="sub-heading-sb text-grey-100 mt-2">{summary.activeCredits.length}</p>
        </div>
      </div>

      {listingPlan && (
        <div className="mb-6 rounded-lg border border-grey-900 p-4" data-cy="billing-listing-plan">
          <p className="body-text text-grey-400">Listing plan</p>
          <p className="sub-heading-sb text-grey-100 mt-2 break-words">
            {`Enterprise plan: ${formatDollars(listingPlan.monthlyFeeCents)}/month, ${listingPlan.activeListingCount} of ${listingPlan.includedActiveListings} active listings used`}
          </p>
          {listingPlan.status === 'CANCELLED' && listingPlan.coverageEndsOn && (
            <p className="body-text text-grey-400 mt-2">
              {`Cancelled. Coverage continues through ${listingPlan.coverageEndsOn}; the plan will not renew.`}
            </p>
          )}
        </div>
      )}

      {schedules.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-grey-100 mb-3">Platform fees</h3>
          <div className="overflow-x-auto border border-grey-900 rounded-lg">
            <table className="min-w-full divide-y divide-grey-900">
              <thead className="bg-grey-950">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Contract
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">Rate</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">
                    Monthly fee
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Next billing date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-900">
                {schedules.map((schedule) => (
                  <tr key={schedule.contractId}>
                    <td className="px-4 py-3 body-text text-left">
                      Contract {schedule.contractNumber}
                    </td>
                    <td className="px-4 py-3 body-text text-right">
                      {formatRateBps(schedule.rateBps)}
                    </td>
                    <td className="px-4 py-3 body-text text-right">
                      {formatCents(schedule.monthlyFeeCents)}
                    </td>
                    <td className="px-4 py-3 body-text text-left">
                      {formatCalendarDate(schedule.nextBillingDate)}
                    </td>
                    <td className="px-4 py-3 body-text text-left">
                      {SCHEDULE_STATUS_LABEL[schedule.status] ?? schedule.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-grey-100 mb-3">Invoice history</h3>
      <div className="overflow-x-auto border border-grey-900 rounded-lg">
        <table className="min-w-full divide-y divide-grey-900">
          <thead className="bg-grey-950">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Period</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                Engagement
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">Fee</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">Credit</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">
                Commitment applied
              </th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">Due</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Invoice</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-900">
            {history.length === 0 ? (
              <tr>
                <td className="px-4 py-6 body-text text-grey-400" colSpan={10}>
                  No billing events yet.
                </td>
              </tr>
            ) : (
              history.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 body-text">{formatDate(row.createdAt)}</td>
                  <td className="px-4 py-3 body-text">
                    {describeHistoryType(row)}
                    {row.description && (
                      <span className="block text-sm text-grey-400">{row.description}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 body-text">{describeHistoryPeriod(row)}</td>
                  <td className="px-4 py-3 body-text">{row.engagementType}</td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(row.grossAmountCents)}
                  </td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(row.creditAppliedCents)}
                  </td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(row.commitmentAppliedCents ?? 0)}
                  </td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(row.netAmountCents)}
                  </td>
                  <td className="px-4 py-3 body-text">{row.status}</td>
                  <td className="px-4 py-3 body-text">
                    {row.hostedInvoiceUrl ? (
                      <a className="text-primary underline" href={row.hostedInvoiceUrl}>
                        View
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
